import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
 Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { buildWaUrl, checkinMessage } from "@/lib/whatsapp";

function fmtDT(iso) {
 if (!iso) return "";
 const d = new Date(iso);
 return d.toLocaleString("pt-BR", {
 day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
 });
}

function legStatus(iso) {
 if (!iso) return null;
 const hours = (new Date(iso).getTime() - Date.now()) / 36e5;
 if (hours<= 0) return { label: "Voo já partiu", pill: "pill-slate", icon: "🛬" };
 if (hours<= 48) return {
 label: `Check-in liberado · ${Math.max(0, Math.round(hours))}h até partida`,
 pill: "pill-green",
 icon: "✅",
 ready: true,
 };
 const days = Math.floor(hours / 24);
 const remH = Math.round(hours - 48);
 return {
 label: days >= 2
 ? `Libera em ${days} dia(s)`
 : `Libera em ~${remH}h`,
 pill: "pill-amber",
 icon: "",
 ready: false,
 };
}

function LegRow({ title, datetime_at, origin, destination, passenger, emission }) {
 if (!datetime_at) return null;
 const st = legStatus(datetime_at);
 const waUrl = passenger?.phone
 ? buildWaUrl(passenger.phone, checkinMessage(passenger, { ...emission, origin, destination, departure_at: datetime_at }))
 : "";
 return (<div className="rounded-lg border border-slate-200 p-3" data-testid={`checkin-leg-${emission.emission_id}-${title}`}><div className="flex items-start justify-between gap-2"><div><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</div><div className="text-sm font-bold text-slate-900 mt-0.5">{origin}<span className="text-blue-500">→</span>{destination}</div><div className="text-xs text-slate-500 mt-0.5">{fmtDT(datetime_at)}</div></div>{st && (<span className={`pill ${st.pill}`}>{st.icon} {st.label}</span>)}</div>{st?.ready && waUrl && (<div className="mt-2"><a href={waUrl} target="_blank" rel="noreferrer"
 data-testid={`checkin-wa-${emission.emission_id}-${title}`}><Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5d] text-white h-7 text-xs">Avisar pelo WhatsApp</Button></a></div>)}</div>);
}

export default function CheckinsDialog({ open, onOpenChange }) {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() =>{
 if (!open) return;
 setLoading(true);
 api.get("/emissions", { params: { filter: "upcoming" } })
 .then(({ data }) =>setItems(data))
 .catch((e) =>toast.error(formatApiError(e.response?.data?.detail)))
 .finally(() =>setLoading(false));
 }, [open]);

 // Sort by closest upcoming leg (any future departure across ida, conexões, volta).
 const nextLegTime = (e) =>{
 const now = Date.now();
 const candidates = [
 e.departure_at,
 e.outbound_connection?.datetime_at,
 e.return_at,
 e.return_connection?.datetime_at,
 ]
 .filter(Boolean)
 .map((iso) =>new Date(iso).getTime())
 .filter((t) =>!isNaN(t) && t >now);
 return candidates.length ? Math.min(...candidates) : Infinity;
 };
 const sorted = [...items].sort((a, b) =>nextLegTime(a) - nextLegTime(b));

 return (<Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="checkins-dialog"><DialogHeader><DialogTitle>Check-ins próximos</DialogTitle><DialogDescription>Veja todos os voos confirmados e quanto tempo falta para liberar o check-in (48h antes da partida).</DialogDescription></DialogHeader>{loading ? (<div className="text-slate-500 text-center py-8" data-testid="checkins-loading">Carregando...</div>) : sorted.length === 0 ? (<div className="text-center py-10" data-testid="checkins-empty"><div className="text-5xl mb-2" aria-hidden></div><div className="font-semibold text-slate-900">Sem voos próximos</div><p className="text-slate-500 text-sm">Quando houver emissões futuras elas aparecem aqui.</p></div>) : (<div className="space-y-4">{sorted.map((e) =>{
 const primary = (e.passengers || [])[0];
 return (<div key={e.emission_id} className="soft-card p-4" data-testid={`checkin-emission-${e.emission_id}`}><div className="flex items-start justify-between gap-2 mb-3"><div><div className="text-sm font-bold text-slate-900">{e.airline} · Localizador<span className="text-[#0b3a82] ml-1">{e.pnr}</span></div><div className="text-xs text-slate-500">{(e.passengers || []).map(p =>p.full_name?.split(" ")[0] || "").filter(Boolean).join(", ") || "Sem passageiros"}</div></div><div className="flex items-center gap-2 shrink-0">{e.ticket_url && (<a href={e.ticket_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0b3a82] bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-md" data-testid={`open-ticket-${e.emission_id}`}>Abrir passagem ↗</a>)}<span className="pill pill-blue">{e.return_at ? "Ida e volta" : "Só ida"}</span></div></div><div className="space-y-2"><LegRow title="Ida" datetime_at={e.departure_at} origin={e.origin} destination={e.destination}
 passenger={primary} emission={e} />{e.outbound_connection && (<LegRow title="Conexão ida"
 datetime_at={e.outbound_connection.datetime_at}
 origin={e.outbound_connection.origin}
 destination={e.outbound_connection.destination}
 passenger={primary} emission={e} />)}
 {e.return_at && (<LegRow title="Volta" datetime_at={e.return_at} origin={e.destination} destination={e.origin}
 passenger={primary} emission={e} />)}
 {e.return_connection && (<LegRow title="Conexão volta"
 datetime_at={e.return_connection.datetime_at}
 origin={e.return_connection.origin}
 destination={e.return_connection.destination}
 passenger={primary} emission={e} />)}</div></div>);
 })}</div>)}</DialogContent></Dialog>);
}
